import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import AnimatedWeatherIcon from './AnimatedWeatherIcon';
import { Droplets, Wind, Gauge, Thermometer, Sun, Waves } from 'lucide-react';
import { getWindDirection, getUVIndexInfo } from '../../utils/weatherHelpers';

// Temperature converter
const toFahrenheit = (celsius) => Math.round((celsius * 9/5) + 32);
const toCelsius = (fahrenheit) => Math.round((fahrenheit - 32) * 5/9);

// UV Index scale helper
const getUVScale = (uvIndex) => {
  const info = getUVIndexInfo(uvIndex);
  return {
    level: info.level,
    color: info.color,
    textColor: info.textColor
  };
};


const CurrentConditionsEnhanced = ({ weather, waterTemp, waterTempStation, currentWaterLevel }) => {
  // Determine if it's night
  const isNight = useMemo(() => {
    if (!weather?.daily?.sunrise?.[0] || !weather?.daily?.sunset?.[0]) return false;
    const now = new Date();
    const sunrise = new Date(weather.daily.sunrise[0]);
    const sunset = new Date(weather.daily.sunset[0]);
    return now < sunrise || now > sunset;
  }, [weather]);

  // Get today's UV index
  const todayUVIndex = useMemo(() => {
    if (!weather?.daily?.time || !weather?.daily?.uv_index_max) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayIndex = weather.daily.time.findIndex(dateStr => {
      const date = new Date(dateStr);
      date.setHours(0, 0, 0, 0);
      return date.getTime() === today.getTime();
    });
    return todayIndex >= 0 ? Math.round(weather.daily.uv_index_max[todayIndex]) : null;
  }, [weather]);

  const uvScale = todayUVIndex !== null ? getUVScale(todayUVIndex) : null;

  if (!weather?.current) return null;

  const windDirection = getWindDirection(weather.current.wind_direction_10m);

  return (
    <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur rounded-xl shadow-lg p-5 border border-orange-100 dark:border-gray-700 animate-on-scroll card-hover hover-lift">
      <h3 className="font-semibold text-lg mb-4 dark:text-gray-100">Current Conditions</h3>
      
      {/* Main weather icon */}
      <div className="flex justify-center mb-4">
        <AnimatedWeatherIcon
          weatherCode={weather.current.weather_code}
          isNight={isNight}
          size={120}
          color={isNight ? '#E6E6FA' : '#FFB347'}
        />
      </div>
      
      {/* 6 main indicators grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {/* Temperature */}
        <div className="text-center p-3 bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-lg">
          <Thermometer className="w-6 h-6 mx-auto mb-1 text-orange-600 dark:text-orange-400" />
          <p className="text-2xl font-bold dark:text-gray-100">
            {Math.round(weather.current.temperature_2m)}°F
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {toCelsius(weather.current.temperature_2m)}°C
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Temperature</p>
        </div>
        
        {/* Humidity */}
        <div className="text-center p-3 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg">
          <Droplets className="w-6 h-6 mx-auto mb-1 text-blue-600 dark:text-blue-400" />
          <p className="text-2xl font-bold dark:text-gray-100">
            {weather.current.relative_humidity_2m}%
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 invisible">-</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Humidity</p>
        </div>
        
        {/* Feels Like */}
        <div className="text-center p-3 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg">
          <Gauge className="w-6 h-6 mx-auto mb-1 text-purple-600 dark:text-purple-400" />
          <p className="text-2xl font-bold dark:text-gray-100">
            {weather.current.apparent_temperature ? Math.round(weather.current.apparent_temperature) : '--'}°F
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {weather.current.apparent_temperature ? toCelsius(weather.current.apparent_temperature) : '--'}°C
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Feels Like</p>
        </div>
        
        {/* Wind with Direction */}
        <div className="text-center p-3 bg-gradient-to-br from-gray-50 to-slate-50 dark:from-gray-900/20 dark:to-slate-900/20 rounded-lg">
          <Wind className="w-6 h-6 mx-auto mb-1 text-gray-600 dark:text-gray-400" />
          <p className="text-2xl font-bold dark:text-gray-100">
            {Math.round(weather.current.wind_speed_10m)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            mph {windDirection}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Wind</p>
        </div>
        
        {/* UV Index with Scale */}
        <div className="text-center p-3 bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-lg">
          <Sun className="w-6 h-6 mx-auto mb-1 text-yellow-600 dark:text-yellow-400" />
          {todayUVIndex !== null ? (
            <>
              <p className="text-2xl font-bold dark:text-gray-100">{todayUVIndex}</p>
              <p className={`text-xs font-medium ${uvScale.textColor}`}>
                {uvScale.level}
              </p>
              {/* UV Scale bar */}
              <div className="mt-1 h-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${uvScale.color} transition-all duration-500`}
                  style={{ width: `${Math.min(todayUVIndex * 9, 100)}%` }}
                />
              </div>
            </>
          ) : (
            <>
              <p className="text-2xl font-bold dark:text-gray-100">--</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">No data</p>
            </>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">UV Index</p>
        </div>
        
        {/* Water Temp with Buoy */}
        <div className="text-center p-3 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg">
          <Waves className="w-6 h-6 mx-auto mb-1 text-teal-600 dark:text-teal-400" />
          <p className="text-2xl font-bold dark:text-gray-100">
            {waterTemp ? `${Math.round(waterTemp)}°F` : '--°F'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {waterTemp ? `${toCelsius(waterTemp)}°C` : '--°C'}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate px-1">
            {waterTempStation || 'Water Temp'}
          </p>
        </div>
      </div>
      
      {/* Additional details */}
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-3 gap-2 text-xs">
          {weather.current.pressure_msl && (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400">Pressure</p>
              <p className="font-medium dark:text-gray-200">{(weather.current.pressure_msl * 0.02953).toFixed(2)}"</p>
            </div>
          )}
          {weather.current.cloud_cover !== undefined && (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400">Cloud Cover</p>
              <p className="font-medium dark:text-gray-200">{weather.current.cloud_cover}%</p>
            </div>
          )}
          {currentWaterLevel !== null && (
            <div className="text-center">
              <p className="text-gray-500 dark:text-gray-400">Water Level</p>
              <p className="font-medium dark:text-gray-200">{currentWaterLevel.toFixed(1)} ft</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CurrentConditionsEnhanced.propTypes = {
  weather: PropTypes.object,
  waterTemp: PropTypes.number,
  waterTempStation: PropTypes.string,
  currentWaterLevel: PropTypes.number
};

export default CurrentConditionsEnhanced;